<script lang="ts">
import GenericButton from "@/Components/Generics/GenericButton.vue";
import GenericModal from "@/Components/Generics/GenericModal.vue";
import MainPage from "@/Components/MainPage.vue";
import PaginationNew from "@/Components/PaginationNew.vue";
import Authenticated from "@/Layouts/AuthenticatedSchool.vue";
import { EventSearchResultsWithoutData, Policies } from "@/Types";
import { EventId } from "@/Types/event";
import axios from "axios";
import { defineComponent, PropType } from "vue";

export default defineComponent({
    name: "SchoolDashboardVue",
    components: {
        MainPage,
        PaginationNew,
        GenericButton,
        GenericModal,
    },
    layout: Authenticated,
    props: {
        events: {
            type: Object as PropType<EventSearchResultsWithoutData>,
            required: true,
        },
        policies: {
            type: Object as PropType<Policies>,
            required: true,
        },
    },
    data() {
        return {
            page: 1,

            loading: false,
            deleteEventModalOpen: false,
            deleteEventId: null as number,
            deleteAccountModalOpen: false,
        };
    },
    methods: {
        clickRoute(hrefRoute: string): void {
            this.$inertia.visit(route(hrefRoute));
        },

        openDeleteEventModal(eventId: EventId): void {
            this.deleteEventModalOpen = true;
            this.deleteEventId = eventId;
        },
        closeDeleteEventModal(): void {
            this.deleteEventModalOpen = false;
            this.deleteEventId = null;
        },

        openDeleteAccountModal(): void {
            this.deleteAccountModalOpen = true;
        },
        closeDeleteAccountModal(): void {
            this.deleteAccountModalOpen = false;
        },

        loadPage(page = false) {
            this.$inertia.reload({
                data: { page: page === false ? this.events.current_page : page },
                only: ["events"],
            });
        },

        deleteAction(): void {
            axios
                .delete(route("school.events.destroy", { company: this.deleteEventId }))
                .then(() => {
                    this.$inertia.reload({
                        data: { page: this.events.current_page },
                        only: ["events"],
                    });
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading = false;
                    this.closeDeleteEventModal();
                });
        },
        deleteAccountAction(): void {
            axios
                .post(route("user.delete"))
                .then(() => {
                    this.$inertia.visit("/");
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading = false;
                    this.closeDeleteAccountModal();
                });
        },
    },
});
</script>

<template>
    <MainPage :title="$trans('navigation.nav_school_dashboard')">
        <div class="flex flex--width flex--v flex--valign-c">
            <table class="table table-dark table-striped">
                <thead>
                    <tr>
                        <th scope="col">{{ $trans("company.id") }}</th>
                        <th scope="col">{{ $trans("company.name") }}</th>
                        <th scope="col">{{ $trans("event.organizer") }}</th>
                        <th scope="col">{{ $trans("event.starting_at") }}</th>
                        <th scope="col">{{ $trans("event.ending_at") }}</th>
                        <th scope="col" class="text-center">{{ $trans("company.active") }}</th>
                        <th scope="col" class="text-center">{{ $trans("acp.options") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="event in events.data" :key="event.id">
                        <th scope="row">{{ event.id }}</th>
                        <td>{{ event.name }}</td>
                        <td>{{ event.organizer_name }}</td>
                        <td>{{ event.starting_at }}</td>
                        <td>{{ event.ending_at }}</td>
                        <td class="text-center">
                            <i v-if="event.active" class="fas fa-check"></i>
                            <i v-else class="fas fa-times"></i>
                        </td>
                        <td class="text-center">
                            <inertia-link v-if="policies.EVENT_CREATE" :href="route('school.events.edit', { event: event.id })" class="text-light"><i class="far fa-edit fa-lg"></i></inertia-link>
                            <a v-if="policies.EVENT_DELETE" class="text-light" @click="openDeleteEventModal(event.id)"><i class="far fa-trash-alt fa-lg"></i></a>
                        </td>
                    </tr>
                </tbody>
            </table>

            <PaginationNew v-if="events.data.length > 0" :pagination-data="events" class="pagination-new" @paginate="loadPage" />
            <!-- <pagination ref="pagination" v-model="events.current_page" :options="paginationOptions" :records="events.total" :per-page="events.per_page" @paginate="loadPage()" /> -->
        </div>

        <div class="buttons-flex-list flex flex--width flex--h flex--halign-c flex--valign-c flex--gap-10">
            <GenericButton :title="$trans('acp.event_create')" @click="clickRoute('school.events.create')" />
            <GenericButton :title="$trans('auth.account_delete')" :danger="true" @click="openDeleteAccountModal()" />
        </div>

        <GenericModal v-if="deleteEventModalOpen" @closed="closeDeleteEventModal()">
            <template #title>{{ $trans("acp.title_modal_delete_event") }}</template>
            <template #content>
                <div class="flex flex--v flex--valign-c text text--size-18 text--height-24 text--primary-b text--medium text--center">{{ $trans("acp.body_modal_delete_event") }}</div>
            </template>
            <template #buttons>
                <GenericButton :title="$trans('acp.close')" :disabled="loading" :circular="true" :click-stop="false" />
                <GenericButton :title="$trans('acp.delete')" :disabled="loading" :circular="true" :danger="true" @click.stop="deleteAction()" />
            </template>
        </GenericModal>

        <GenericModal v-if="deleteAccountModalOpen" @closed="closeDeleteAccountModal()">
            <template #title>{{ $trans("auth.title_modal_delete_account") }}</template>
            <template #content>
                <div class="flex flex--v flex--valign-c text text--size-18 text--height-24 text--primary-b text--medium text--center">{{ $trans("auth.body_modal_delete_account") }}</div>
            </template>
            <template #buttons>
                <GenericButton :title="$trans('acp.close')" :disabled="loading" :circular="true" :click-stop="false" />
                <GenericButton :title="$trans('auth.account_delete')" :disabled="loading" :circular="true" :danger="true" @click.stop="deleteAccountAction()" />
            </template>
        </GenericModal>
    </MainPage>
</template>

<style lang="scss" scoped>
tbody i {
    margin-left: 10px;
}
@include media-breakpoint-down(md) {
    .buttons-flex-list {
        flex-direction: column;
    }
}
</style>
